import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { ApiHelperService } from 'src/app/core/helpers/api-helper.service';
import { PublicIpService } from 'src/app/core/services/public-ip.service';
import {
    BusinessDetailPaidResponse,
    BusinessDetailTrialResponse,
    BusinessListPaidResponse,
    BusinessListTrialResponse,
    PinnedCompanyListQueryParams,
    PinnedCompanyListResponse,
    PinnedSankeyCompanyListQueryParams,
    PinnedSankeyCompanyListResponse,
    PinnedSankeyCompanyToggleResponse,
} from '../../shared/models';
import { ApiErrorResponse } from '../../shared/models/base-response.interface';
import { SubmitCompanyFeedbackPayload } from './company/company-detail/company-feedback.types';
import { SankeyGraphResponse, QuickInsightResponse } from '../../shared/models/data-insight.interface';
import {
    CreateDisclosureJobResponse,
    DisclosureJobDetailResponse,
    DisclosureJobListResponse,
    DisclosureTarget,
} from './company/company-detail/company-crawl-tab/company-crawl-tab.types';
import { CompanyChartAnswer } from './company/company-detail/company-chat-knowledge/company-chat-knowledge.types';
import { environment } from 'src/environments/environment';

// TODO: temporary hard-coded host — replace once BE publishes the chart API on a stable domain
const COMPANY_CHART_ASK_URL = 'https://dpf-datacore-chart-qa.mobifi.vn/api/v2/ask';

@Injectable({
    providedIn: 'root'
})
export class ServicesService {

    constructor(
        private api: ApiHelperService,
        private publicIpService: PublicIpService,
        private http: HttpClient,
    ) {
    }

    private getTrialIp(): string {
        return localStorage.getItem('test') === 'true'
            ? this.publicIpService.getRandomPublicIp()
            : this.publicIpService.getPublicIp();
    }

    reportAddress(body: any): Observable<any> {
        return this.api.create(environment.REPORT_ADDRESS, body);
    }

    // Async await below
    async getAllBusiness(params: any): Promise<BusinessListPaidResponse | ApiErrorResponse> {
        return await firstValueFrom(this.api.getAll(environment.BUSINESS_GET_ALL, params));
    }

    async getAllBusinessTrial(params: any): Promise<BusinessListTrialResponse | ApiErrorResponse> {
        return await firstValueFrom(this.api.getAll(environment.BUSINESS_GET_ALL_TRIAL, params, { ip: this.getTrialIp() }));
    }

    async getBusinessDetail(taxCode: string): Promise<BusinessDetailPaidResponse | ApiErrorResponse> {
        return await firstValueFrom(this.api.getChildren(environment.BUSINESS_GET_BY_TAX_CODE, { taxCode }));
    }

    async getBusinessDetailTrial(taxCode: string): Promise<BusinessDetailTrialResponse | ApiErrorResponse> {
        return await firstValueFrom(this.api.getChildren(environment.BUSINESS_GET_BY_TAX_CODE_TRIAL, { taxCode }, {}, { ip: this.getTrialIp() }));
    }

    async getProvinceFilter(): Promise<any> {
        return await firstValueFrom(this.api.getAll(environment.PROVINCE_GET_FILTER));
    }

    async getIndustryFilter(): Promise<any> {
        return await firstValueFrom(this.api.getAll(environment.INDUSTRY_GET_FILTER));
    }

    async getCountryFilter(): Promise<any> {
        return await firstValueFrom(this.api.getAll(environment.COUNTRY_GET_FILTER));
    }

    /** GET /data/api/pinned-company/list — optional query filters + pagination. */
    async getPinnedCompanyList(params?: PinnedCompanyListQueryParams): Promise<PinnedCompanyListResponse> {
        return await firstValueFrom(this.api.getAll(environment.PINNED_COMPANY_GET_LIST, params));
    }

    /** POST /data/api/pinned-company/:taxCode/toggle — body is only `{ isPinned }`. */
    async pinCompany(taxCode: string, isPinned: boolean): Promise<any> {
        const body = { isPinned };
        const res = await firstValueFrom(this.api.createChildren(environment.PINNED_COMPANY_TOGGLE, { taxCode }, body));
        // Rely on status; httpCode may be number or string across environments
        if (res?.status !== true) {
            throw new Error(res?.message ?? 'Pin company request failed');
        }
        return res;
    }

    /** GET /data/api/favourite-sankey-company/list — data insights pinned companies. */
    async getPinnedSankeyCompanyList(params?: PinnedSankeyCompanyListQueryParams): Promise<PinnedSankeyCompanyListResponse> {
        return await firstValueFrom(this.api.getAll(environment.PINNED_SANKEY_COMPANY_GET_LIST, params));
    }

    /** POST /data/api/favourite-sankey-company/:taxCode/toggle — body is only `{ isFavourite }`. */
    async pinSankeyCompany(companyCode: string, isFavourite: boolean): Promise<PinnedSankeyCompanyToggleResponse> {
        const body = { isFavourite };
        const res = await firstValueFrom(this.api.createChildren(environment.PINNED_SANKEY_COMPANY_TOGGLE, { taxCode: companyCode }, body));
        if (res?.status !== true) {
            throw new Error(res?.message ?? 'Pin sankey company request failed');
        }
        return res;
    }

    async getAllEkycOffer(params: any): Promise<any> {
        return await firstValueFrom(this.api.getAll(environment.GET_ALL_EKYC_OFFER, params));
    }

    async getAllEmbeddingOffer(params: any): Promise<any> {
        return await firstValueFrom(this.api.getAll(environment.GET_ALL_EMBEDDING_OFFER, params));
    }

    async getPacketApiList(keyMap: any = {}): Promise<any> {
        return await firstValueFrom(this.api.getChildren(environment.GET_PACKET_API_LIST, keyMap));
    }

    async getCompanyGraphData(taxCode: string): Promise<any> {
        return await firstValueFrom(this.api.getChildren(environment.COMPANY_GRAPH_GET_BY_TAX_CODE, { taxCode }));
    }

    async getCompanyGraphDataTrial(taxCode: string): Promise<any> {
        return await firstValueFrom(this.api.getChildren(environment.COMPANY_GRAPH_GET_BY_TAX_CODE_TRIAL, { taxCode }, {}, { ip: this.getTrialIp() }));
    }

    async getCompanyGraphNodeById(nodeId: string): Promise<any> {
        return await firstValueFrom(this.api.getAll(environment.COMPANY_GRAPH_GET_NODE_BY_ID, { node_id: nodeId }));
    }

    async getCompanyGraphNodeByIdTrial(nodeId: string): Promise<any> {
        return await firstValueFrom(this.api.getAll(environment.COMPANY_GRAPH_GET_NODE_BY_ID_TRIAL, { node_id: nodeId }, { ip: this.getTrialIp() }));
    }

    async getCompanyStatsCount(): Promise<any> {
        return await firstValueFrom(this.api.getAll(environment.COMPANY_STATS_COUNT));
    }

    async submitCompanyFeedback(payload: SubmitCompanyFeedbackPayload): Promise<any> {
        return await firstValueFrom(this.api.create(environment.POST_FEEDBACK_COMPANY_DATA, payload));
    }

    async getCompanyNewsArticles(taxCode: string, page = 1, size = 10): Promise<any> {
        return await firstValueFrom(this.api.getChildren(environment.COMPANY_NEWS_ARTICLES, { taxCode }, { page, size }));
    }

    async getCompanyNewsArticlesTrial(taxCode: string, page = 1, size = 10): Promise<any> {
        return await firstValueFrom(this.api.getChildren(environment.COMPANY_NEWS_ARTICLES_TRIAL, { taxCode }, { page, size }, { ip: this.getTrialIp() }));
    }

    async askCompanyKnowledge(taxCode: string, question: string): Promise<any> {
        return await firstValueFrom(this.api.create(environment.COMPANY_KNOWLEDGE_CHAT, { taxCode, question }));
    }

    async askCompanyKnowledgeTrial(taxCode: string, question: string): Promise<any> {
        return await firstValueFrom(this.api.create(environment.COMPANY_KNOWLEDGE_CHAT_TRIAL, { taxCode, question }, undefined, { ip: this.getTrialIp() }));
    }

    // Plain HttpClient: RestConnector's gateway error handling (401 → clear session + logout, error toasts)
    // must not react to responses from this external service. Sends the same auth/context as the old ask APIs.
    async askCompanyChart(taxCode: string, question: string, isTrial: boolean): Promise<CompanyChartAnswer> {
        const headers: Record<string, string> = {};
        const token = localStorage.getItem('token');
        if (token) headers['token'] = token;
        const ip = isTrial ? this.getTrialIp() : '';
        if (ip) headers['ip'] = ip;
        return await firstValueFrom(
            this.http.post<CompanyChartAnswer>(COMPANY_CHART_ASK_URL, { taxCode, question }, { headers })
        );
    }

    async getCompanyKnowledgeHistory(): Promise<any> {
        return await firstValueFrom(this.api.getAll(environment.COMPANY_KNOWLEDGE_HISTORY));
    }

    async clearCompanyKnowledgeHistory(): Promise<any> {
        return await firstValueFrom(this.api.delete(environment.COMPANY_KNOWLEDGE_HISTORY));
    }

    async getAllDataInsights(params: any): Promise<any> {
        return await firstValueFrom(this.api.getAll(environment.DATA_INSIGHTS_GET_COMPANY_LIST, params));
    }

    async getSankeyGraph(params: Record<string, string | string[] | undefined>): Promise<SankeyGraphResponse> {
        return await firstValueFrom(this.api.getAll(environment.DATA_INSIGHTS_GET_SANKEY_GRAPH, params));
    }

    async getSankeyGraphTrial(params: Record<string, string | string[] | undefined>): Promise<SankeyGraphResponse> {
        return await firstValueFrom(this.api.getAll(environment.DATA_INSIGHTS_GET_SANKEY_GRAPH_TRIAL, params, { ip: this.getTrialIp() }));
    }

    async getQuickInsights(params: { symbol: string; year: string; quarter?: string }): Promise<QuickInsightResponse> {
        return await firstValueFrom(this.api.getAll(environment.DATA_INSIGHTS_GET_QUICK_INSIGHTS, params));
    }

    async getQuickInsightsTrial(params: { symbol: string; year: string; quarter?: string }): Promise<QuickInsightResponse> {
        return await firstValueFrom(this.api.getAll(environment.DATA_INSIGHTS_GET_QUICK_INSIGHTS_TRIAL, params, { ip: this.getTrialIp() }));
    }

    async createDisclosureJob(
        taxCode: string,
        targetCrawl: DisclosureTarget,
    ): Promise<CreateDisclosureJobResponse | ApiErrorResponse> {
        return await firstValueFrom(
            this.api.createChildren(environment.COMPANY_DISCLOSURE_REQUEST, { taxCode }, { targetCrawl }),
        );
    }

    async getDisclosureJobList(
        taxCode: string,
        page = 1,
        size = 10,
    ): Promise<DisclosureJobListResponse | ApiErrorResponse> {
        return await firstValueFrom(
            this.api.getChildren(environment.COMPANY_DISCLOSURE_JOBS, { taxCode }, { page, size }),
        );
    }

    async getDisclosureJobDetail(
        jobUuid: string,
    ): Promise<DisclosureJobDetailResponse | ApiErrorResponse> {
        return await firstValueFrom(
            this.api.getChildren(environment.COMPANY_DISCLOSURE_JOB_DETAIL, { jobUuid }),
        );
    }

    /** GET a disclosure file as a Blob; adds the auth token header via ApiHelperService. */
    getDisclosureFileBlob(path: string): Observable<any> {
        return this.api.getBlobResponse(path);
    }
}
